import React, { useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Menu,
    MenuItem
} from '@mui/material';
import SortIcon from '../../../assets/images/Dashboard/chevron-down.svg';
import ascToDesc from '../../../assets/images/CustomerManagement/ascToDesc.svg';
import descToAsc from '../../../assets/images/CustomerManagement/descToAsc.svg';
import SbActionDropdownButton from '../../Buttons/SbActionDropdownButton';
import StatusBadgeWithDropdown from '../../Buttons/StatusBadgeWithDropdown';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import Skeleton from 'react-loading-skeleton';
import noDataFound from '../../../assets/images/no-data-found.svg';
import PaginationComponent from '../../PaginationComponent';

const columns = [
    { id: 'MaterialTypeCode', label: 'MATERIAL TYPE CODE', align: 'left', minWidth: '150px' },
    { id: 'Name', label: 'MATERIAL TYPE NAME', align: 'left', minWidth: '200px' },
    { id: 'MaterialTypeDescription', label: 'DESCRIPTION', align: 'left', minWidth: '250px' },
    { id: 'action', label: 'ACTION', align: 'left', minWidth: '80px', sortable: false },
];

const MaterialTypeTable = ({ totalDataCount, selectedColumns, handleOpenModal, handleOpenViewModal, loading, materialTypeData = [], page, setPage, rowsPerPage, setRowsPerPage, moduleData, }) => {
    const SortableHead = SortableContainer(({ children }) => {
        return (
            <TableHead>
                <TableRow>{children}</TableRow>
            </TableHead>
        );
    });

    const SortableCell = SortableElement(({ value }) => {
        return <TableCell>{value}</TableCell>;
    });


    // Get selected columns (based on `selectedColumns` prop)
    const filteredColumns = columns.filter((column) =>
        selectedColumns.includes(column.label)
    );

    const getStoredColumnsOrder = () => {
        const savedOrder = localStorage.getItem('materialTypeColumnsOrder');
        return savedOrder ? JSON.parse(savedOrder) : columns.map((col) => col.id);
    };

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentSortColumn, setCurrentSortColumn] = useState(null);
    const [actionAnchorEl, setActionAnchorEl] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [paginatedData, setPaginatedData] = useState([]);
    const [columnsOrder, setColumnsOrder] = useState(getStoredColumnsOrder());

    const handleRequestSort = (event, property) => {
        setCurrentSortColumn(property);
        setAnchorEl(event.currentTarget);
    };

    const handleSortOrderChange = (order) => {
        setOrder(order);
        setOrderBy(currentSortColumn);
        setAnchorEl(null);
    };

    const handleActionClick = (event, customer) => {
        setSelectedCustomer(customer);
        setActionAnchorEl(event.currentTarget);
    };

    const handleActionClose = () => {
        setActionAnchorEl(false);
    };

    const handleMenuItemClick = (action) => {
        if (action === 'edit') {
            console.log('Edit Details for:', selectedCustomer);
            // Implement edit functionality here
        } else if (action === 'view') {
            console.log('View Details for:', selectedCustomer);
            // Implement view functionality here
        }
        handleActionClose();
    };

    const onReorderEnd = ({ oldIndex, newIndex }) => {
        const newOrder = Array.from(columnsOrder);
        const [moved] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, moved);
        setColumnsOrder(newOrder);
        localStorage.setItem('materialTypeColumnsOrder', JSON.stringify(newOrder)); // Store new order
    };

    useEffect(() => {
        if (!materialTypeData || materialTypeData.length === 0) {
            console.warn("⚠️ No Material Type Data Found!");
            setPaginatedData([]);
            return;
        }

        // Sort data based on order & orderBy
        const sorted = [...materialTypeData].sort((a, b) => {
            const orderModifier = order === 'asc' ? 1 : -1;
            return a[orderBy] < b[orderBy] ? -1 * orderModifier : 1 * orderModifier;
        });

        // Calculate pagination
        const maxPage = Math.ceil(sorted.length / rowsPerPage);
        const newPage = Math.min(page, maxPage - 1); // Prevents out-of-range pages

        const start = newPage * rowsPerPage;
        const end = start + rowsPerPage;
        const paginated = sorted.slice(start, end);

        setPaginatedData(paginated);
    }, [materialTypeData?.length, order, orderBy, page, rowsPerPage]);

    const handlePageChange = (newPage) => {
        setPage(newPage - 1); // Handle 1-based to 0-based page index for slicing
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to the first page when rows per page changes
    };

    return (
        <>
            {/* {moduleData?.CanView === true ? ( */}
            <>
                <TableContainer component={Paper} sx={{ maxHeight: 440 }} className='table-container'>
                    <Table>
                        <SortableHead axis="x" onSortEnd={onReorderEnd}>
                            {columnsOrder.map((columnId, index) => {
                                const column = columns.find(col => col.id === columnId);
                                if (!column) return null; // Skip if column doesn't exist

                                // Only display columns that are in filteredColumns
                                if (!filteredColumns.find(col => col.id === columnId)) {
                                    return null;
                                }
                                return (
                                    <SortableCell key={column.id} index={index} value={
                                        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minWidth: column.minWidth }} className='table-header-div'>
                                            {column.label}
                                            {column.sortable !== false && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => handleRequestSort(event, column.id)}
                                                >
                                                    <img src={SortIcon} alt="Sort" />
                                                </IconButton>
                                            )}
                                        </div>
                                    } />
                                );
                            })}
                        </SortableHead>
                        <TableBody>
                            {
                                loading ? (
                                    [...Array(rowsPerPage)].map((_, index) => (
                                        <TableRow key={index}>
                                            {columns.map((column) => (
                                                <TableCell key={column.id} align={column.align}>
                                                    <Skeleton height={40} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : paginatedData.length === 0 ? (
                                    // Display "No Data Found" row if no data is present in the paginated data
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            <img src={noDataFound} alt="" style={{ height: "250px", width: "280px" }} />
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedData.map((customer) => (
                                        <TableRow key={customer.id}>
                                            {columnsOrder.map((columnId) => {
                                                const column = columns.find((col) => col.id === columnId);
                                                if (!column || !filteredColumns.find(col => col.id === columnId)) {
                                                    return null; // Skip if column doesn't exist or not selected
                                                }
                                                return (
                                                    <TableCell key={column.id} align={column.align}>
                                                        {column.id === 'Status' ? (
                                                            <span
                                                                className={`status-badge ${customer.Status === 'Active' ? 'complete-status no-dropdown-badge' : 'cancel-status no-dropdown-badge'}`}
                                                            >
                                                                {customer.Status}
                                                            </span>
                                                        ) : column.id === 'action' ? (
                                                            <SbActionDropdownButton
                                                                options={[{ type: 'Edit' }, { type: 'View' }]}
                                                                openAddModal={() => { handleOpenModal(customer.MaterialTypeID) }}
                                                                addBulkModel={() => { handleOpenViewModal(customer.MaterialTypeID) }}
                                                            />
                                                        ) : (
                                                            customer[column.id]
                                                        )}
                                                    </TableCell>

                                                )
                                            })}
                                        </TableRow>
                                    )))
                            }
                        </TableBody>
                    </Table>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                    >
                        <MenuItem onClick={() => handleSortOrderChange('asc')}>
                            <img src={ascToDesc} alt="" />&nbsp;&nbsp;
                            Ascending to Descending
                        </MenuItem>
                        <MenuItem onClick={() => handleSortOrderChange('desc')}>
                            <img src={descToAsc} alt="" />&nbsp;&nbsp;
                            Descending to Ascending
                        </MenuItem>
                    </Menu>
                </TableContainer>
                {/* Use PaginationComponent */}
                <PaginationComponent
                    totalItems={totalDataCount}
                    itemsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            </>
            {/* ) : (
                <div className='d-flex align-items-center justify-content-center p-3'>
                    <img src={noModuleAccess} alt="" />
                </div>
            )} */}
        </>
    );
};

export default MaterialTypeTable;
